import { Autocomplete, Card, CardContent, Grid, Stack, TextField, Typography } from '@mui/material';
import { concat, Dictionary, flatMap, isArray, isEmpty, map, size } from 'lodash';
import React, { useMemo, useState } from 'react';

import { editStudyReportJobChart } from 'api/jobs';
import Loader, { LoadingStage } from 'components/Loader';
import ChartShareMenu from 'components/Pages/StudyReport/ChartShareMenu';
import JobResultSlideLabelModeSelect from 'components/Pages/StudyReport/JobResultSlideLabelModeSelect';
import StudyReportCardHeader from 'components/Pages/StudyReport/StudyReportCardHeader';
import StudyReportDescriptionMarkdown from 'components/Pages/StudyReport/StudyReportDescriptionMarkdown';
import ThemedPlotlyFigure from 'components/Plotly/ThemedPlotlyFigure';
import CardContentEditable from 'components/StudiesDashboard/CardContentEditable';
import { PlotlyFigure } from 'interfaces/agentSession';
import { CommonJobResult } from 'interfaces/job';
import { JobResultSourceConfig } from 'interfaces/jobResultMetadata';
import { Permission } from 'interfaces/permissionOption';
import { useSnackbar } from 'notistack';
import { useParsedBrackets } from 'utils/features/getFeaturesKeysObjectsWebWorker/useParsedBrackets';
import {
  recursiveGetBracketsFromObject,
  recursiveReplaceFeaturesSyntaxWithDisplayNames,
} from 'utils/formatBrackets/replaceBracketsWithDisplayName';
import { useJobResultAugmentation } from 'utils/jobResultAugmentation';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';
import { LinksSection } from './LinksSection';

interface Props {
  result: PlotlyFigure;
  isLoading: boolean;
  jobId: string;
  studyId?: string;
  showError: boolean;
  resultKey: string;
  resultTitle: string;
  selectedGroupResultKey?: string;
  groupResults?: string[];
  resultDescription?: string;
  links: CommonJobResult['links'];
  sourceConfig?: JobResultSourceConfig;
  groupKeyToTitle?: (key: string) => string;
  setSelectedResult: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const DEFAULT_CHART_HEIGHT = 500;
export const CHART_GRID_HEIGHT = DEFAULT_CHART_HEIGHT + 100;

const UNKNOWN_ORCHESTRATION_ID = '';

export const JobResultChart: React.FC<React.PropsWithChildren<Props>> = ({
  result,
  isLoading,
  studyId,
  jobId: _jobId,
  showError,
  resultKey: _resultKey,
  resultTitle,
  resultDescription,
  selectedGroupResultKey,
  links,
  sourceConfig,
  groupResults,
  groupKeyToTitle,
  setSelectedResult,
}) => {
  const [chartEl, setChartEl] = useState<HTMLElement | null>(null);
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const canEditChartsDescriptions = hasPermission(Permission.EditStudyReportJobCharts);
  const editableChartId = selectedGroupResultKey || _resultKey;

  const editDescriptionMutation = useMutationWithErrorSnackbar({
    mutationFn: editStudyReportJobChart,
    mutationDescription: 'edit chart description',
    onSuccess: () => {
      enqueueSnackbar('Chart description updated', {
        variant: 'success',
      });
    },
  });

  const featureSyntaxForChartLayout = (result?.layout as any)?.meta?.features_syntax as Dictionary<any> | undefined;

  const bracketsToParse = useMemo(
    () =>
      flatMap(
        concat(
          featureSyntaxForChartLayout ? [featureSyntaxForChartLayout] : [],
          isArray(result?.data) ? result?.data : result?.data ? [result?.data] : []
        ),
        recursiveGetBracketsFromObject
      ),
    [featureSyntaxForChartLayout, result?.data]
  );

  const { data: parsedBrackets, isLoading: isParsingBrackets } = useParsedBrackets({
    bracketsToParse,
    addStainToFeatureName: true,
    studyId,
    orchestrationId: UNKNOWN_ORCHESTRATION_ID,
  });

  const featureResolvedFigure: PlotlyFigure = useMemo(() => {
    const meta = (result?.layout as any)?.meta;

    if (meta?.features_syntax) {
      meta.features_syntax = recursiveReplaceFeaturesSyntaxWithDisplayNames(meta?.features_syntax, parsedBrackets);
    }

    const themedData = isArray(result?.data)
      ? map(result?.data, (dataEntry) => recursiveReplaceFeaturesSyntaxWithDisplayNames(dataEntry, parsedBrackets))
      : recursiveReplaceFeaturesSyntaxWithDisplayNames(result?.data, parsedBrackets);

    return {
      data: (isArray(themedData) ? themedData : themedData ? [themedData] : []) as Record<string, unknown>[],
      layout: { ...(result?.layout || {}), meta, autosize: true } as Record<string, unknown>,
    };
  }, [result?.layout, result?.data, parsedBrackets]);

  const {
    augmentedFigure,
    augmentedDescription,
    showSlideLabelControl,
    slideLabelMode,
    slideLabelOptions,
    setSlideLabelMode,
  } = useJobResultAugmentation({
    studyId,
    sourceConfig,
    figure: featureResolvedFigure,
    description: resultDescription,
  });
  const figure = augmentedFigure || featureResolvedFigure;
  const displayDescription = augmentedDescription ?? resultDescription;

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1}>
          <StudyReportCardHeader
            title={!isLoading || showError ? resultTitle : undefined}
            actions={<ChartShareMenu chartEl={chartEl} figure={figure} title={resultTitle} />}
          />
          {size(groupResults) > 1 && (
            <Autocomplete<string, false, false, false>
              value={selectedGroupResultKey}
              getOptionLabel={groupKeyToTitle}
              options={groupResults}
              onChange={(_event, newSelectedResult) => setSelectedResult(newSelectedResult)}
              clearIcon={false}
              renderInput={(params) => <TextField {...params} variant="standard" />}
            />
          )}
          <LinksSection links={links} studyId={studyId} />
          {showSlideLabelControl ? (
            <JobResultSlideLabelModeSelect
              value={slideLabelMode}
              options={slideLabelOptions}
              onChange={setSlideLabelMode}
            />
          ) : null}
          <Grid item sx={{ overflowX: 'auto', height: DEFAULT_CHART_HEIGHT }}>
            {isLoading || isParsingBrackets ? (
              <Loader stage={LoadingStage.LoadingResults} />
            ) : showError ? (
              <Typography variant="caption" color="warning">
                Failed to query chart data
              </Typography>
            ) : isEmpty(figure.data) ? (
              <Typography variant="caption" color="warning">
                No data
              </Typography>
            ) : (
              <ThemedPlotlyFigure
                figure={figure}
                height={DEFAULT_CHART_HEIGHT}
                onPlotlyInit={setChartEl}
                animateOnUpdate
              />
            )}
          </Grid>
          {displayDescription && !canEditChartsDescriptions && (
            <StudyReportDescriptionMarkdown content={displayDescription} studyId={studyId} sx={{ mt: 1 }} />
          )}
          {canEditChartsDescriptions && (
            <CardContentEditable
              disabled={editDescriptionMutation.isLoading}
              displayContent={
                displayDescription ? (
                  <StudyReportDescriptionMarkdown content={displayDescription} studyId={studyId} sx={{ mt: 1 }} />
                ) : undefined
              }
              savedValue={resultDescription || ''}
              onSave={(newValue: string) =>
                editDescriptionMutation.mutate({
                  jobId: _jobId,
                  chartId: editableChartId,
                  description: newValue,
                })
              }
              onEditModeChange={() => undefined}
              placeholder="Add chart description"
              placeholderDisplay="hover"
              style={{ width: '100%', marginTop: 8, color: 'inherit', fontSize: '0.75rem' }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
